import { create } from "zustand";
import { getError } from "../../utils/getError";
import api, { isAbortError } from "../../utils/api";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface Group {
  id: string;
  name: string;
  description?: string;
  status_id: string;
  devices_ids: string[];
  // Vehicle IDs resolved from devices_ids by the backend (each device.vehicle_id).
  // Use this for truck-yard joins; devices_ids are device document IDs.
  vehicle_ids?: string[];
  monitorista_ids?: string[];
  devicesCount?: number;
  company_id?: string;
  type?: string[];
  // Legal name + RFC identifier, and the patios (yards) tied to this group.
  // yard_ids is constrained to yards whose type intersects the group's type.
  razonSocial?: string;
  rfc?: string;
  // Legacy identifier kept read-only so old documents created before the
  // NIS → RFC rename still surface their value in the edit form.
  nis?: string;
  yard_ids?: string[];
  status: {
    id: string;
    name: string;
  };
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface CreateGroupData {
  name: string;
  description?: string;
  status_id: string;
  devices_ids: string[];
  monitorista_ids?: string[];
  type?: string[];
  razonSocial?: string;
  rfc?: string;
  yard_ids?: string[];
}

export interface UpdateGroupData {
  name?: string;
  description?: string;
  status_id?: string;
  devices_ids?: string[];
  monitorista_ids?: string[];
  type?: string[];
  razonSocial?: string;
  rfc?: string;
  yard_ids?: string[];
}

interface GroupsStore {
  groups: Group[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchGroups: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status_id?: string;
    search?: string;
  }) => Promise<void>;
  createGroup: (
    groupData: CreateGroupData
  ) => Promise<{ success: boolean; data?: Group; error?: string }>;
  updateGroup: (
    groupId: string,
    groupData: UpdateGroupData
  ) => Promise<{ success: boolean; data?: Group; error?: string }>;
  deleteGroup: (
    groupId: string
  ) => Promise<{ success: boolean; error?: string }>;
}

export const useGroupsStore = create<GroupsStore>((set, get) => ({
  groups: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchGroups: async (params = {}) => {
    const fetchGen = getAdminFetchGeneration();

    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/groupList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      if (getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false });
        return;
      }
      set({ groups: data.data || [], meta: data.meta, loading: false });
    } catch (error: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(error)) {
        set({ loading: false });
        return;
      }
      const errorMessage = getError(error);
      set({ error: errorMessage, loading: false });
    }
  },

  createGroup: async (groupData: CreateGroupData) => {
    try {
      const { data } = await api.post("/group", groupData);
      // No actualizamos el estado local, dejamos que fetchGroups lo haga
      // para obtener los datos completos incluyendo status.name
      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error creating group:", err);
      return { success: false, error: getError(err) };
    }
  },

  updateGroup: async (groupId: string, groupData: UpdateGroupData) => {
    try {
      const { data } = await api.put("/group", groupData, {
        params: { id: groupId },
      });
      // No actualizamos el estado local, dejamos que fetchGroups lo haga
      // para obtener los datos completos incluyendo status.name
      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error updating group:", err);
      return { success: false, error: getError(err) };
    }
  },

  deleteGroup: async (groupId: string) => {
    try {
      await api.delete(`/group`, { params: { id: groupId } });

      set((state) => ({
        groups: state.groups.filter((group) => group.id !== groupId),
      }));

      return { success: true };
    } catch (err: any) {
      console.error("Error deleting group:", err);
      return { success: false, error: getError(err) };
    }
  },
}));
